import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import React, {useState} from 'react';
import {Box, Button, Icon, Typography} from '@mui/material';
import {Logout, NotificationImportant} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import {signOut} from 'aws-amplify/auth';
import {useNavigate} from 'react-router-dom';

import {ShowIfRole, useDjangoContext} from '../../contexts/useAuthContext';
import {envs, getEnvironment} from '../../utils/envTest';
import {useDebugContext} from '../../contexts/DebugProvider';
import {darkBlue, themeColors} from '../../theme/Theme';
import {DebugModal} from './DebugModal';
import {RegressionModal} from './RegressionModal';
import VersionRefresh from '../VersionRefresh/VersionRefresh';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {ScrollableModal} from '../Common/ScrollableModal';
import {HeaderSearch} from '../HeaderSearch/HeaderSearch';
import MenuItem from '../MenuItem/MenuItem';

const bgColors: Record<envs, string> = {
  DEV: '#224538',
  PROD: darkBlue,
  TEST: themeColors.coral,
  UAT: themeColors.coral,
};
const textColors: Record<envs, string> = {
  DEV: 'inherit',
  PROD: 'inherit',
  TEST: 'primary',
  UAT: 'primary',
};

export interface HeaderProps {
  disableUrl?: boolean;
  setIsSideMenuOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}
export interface ProfileControlsState {
  adminOpen: boolean;
}

function ProfileControls() {
  const {djangoUser, perms} = useDjangoContext();
  const [testerModal, setTesterModal] = useState(false);
  const [regressionModal, setRegressionModal] = useState(false);
  const {debug, setDebug} = useDebugContext();

  return (
    <>
      <ScrollableModal
        sx={{
          width: '98%',
          mt: 1,
          ml: '1%',

          '& > *': {},
        }}
        size="xlarge"
        open={testerModal}
        onClose={() => setTesterModal(false)}
      >
        <DebugModal
          setTesterModal={setTesterModal}
          djangoUser={djangoUser}
          setDebug={setDebug}
          debug={debug}
          perms={perms}
        />
      </ScrollableModal>

      <ScrollableModal
        sx={{
          width: '98%',
          mt: 1,
          ml: '1%',

          '& > *': {},
        }}
        size="xlarge"
        open={regressionModal}
        onClose={() => setRegressionModal(false)}
      >
        <RegressionModal setRegressionModal={setRegressionModal} />
      </ScrollableModal>

      <Box mr={2} />

      {perms.includes(GlobalPermissionEnum.can_change_business_unit) ||
      perms.includes(GlobalPermissionEnum.can_access_admin_features) ||
      perms.includes(GlobalPermissionEnum.can_access_limited_features) ? (
        <>
          <Button
            onClick={async (e) => {
              if (e.altKey) {
                setDebug((x) => ({...x, forms: !x.forms}));
              } else {
                setTesterModal(true);
              }
            }}
            color="secondary"
            variant="contained"
            data-testid="debug-modal-button"
          >
            Debug
          </Button>
          <Box ml={1} />
          <Button
            onClick={() => setRegressionModal(true)}
            color="secondary"
            variant="contained"
            data-testid="regression-modal-button"
          >
            Regression
          </Button>
        </>
      ) : null}
    </>
  );
}

function HeaderControls(props: HeaderProps) {
  const {disableUrl, setIsSideMenuOpen} = props;
  const {businessUnit, notifications, djangoUser} = useDjangoContext();
  const env = getEnvironment();
  const navigate = useNavigate();
  const {debug: _debug} = useDebugContext();
  return (
    <>
      <Icon
        onClick={() => setIsSideMenuOpen?.((isSideMenuOpen) => !isSideMenuOpen)}
        sx={{
          display: {xs: 'block', sm: 'none'},
          marginRight: 1,
          cursor: 'pointer',
        }}
      >
        <MenuIcon />
      </Icon>
      <Box
        id="toggle-menu"
        onClick={(e) => {
          e.preventDefault();
          if (e.altKey) {
            if (e.shiftKey) {
              setIsSideMenuOpen?.(true);
            } else if (e.ctrlKey) {
              setIsSideMenuOpen?.(false);
            } else {
              setIsSideMenuOpen?.((isSideMenuOpen) => !isSideMenuOpen);
            }
            return;
          }
          if (!disableUrl) {
            navigate({pathname: '/search'});
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <img
          alt="company logo"
          src="/logo.svg"
          style={{
            width: '100px',
            height: '25px',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Box ml={2}>
        <HeaderSearch />
      </Box>
      <Box ml={2}>
        <Typography color={textColors[env]} variant="h6">
          <ShowIfRole roles={['tester', 'dev']}>
            <strong>{businessUnit?.partnershipType}:</strong>
          </ShowIfRole>{' '}
          {businessUnit?.name || ''}(
          {djangoUser.groups?.map((x) => (
            <Typography display="inline" key={x?.name}>
              {x?.name}
            </Typography>
          ))}
          )
        </Typography>
      </Box>
      {notifications.length ? (
        <Box ml={1}>
          {notifications.map((notification) => {
            return (
              <Box
                display="flex"
                alignContent="center"
                justifyContent="flex-start"
                key={notification.id}
              >
                <NotificationImportant fontSize="small" color="warning" />{' '}
                {notification.message || ''}
              </Box>
            );
          })}
        </Box>
      ) : null}
      {env !== 'PROD' ? (
        <Box ml={2}>
          <Typography color={textColors[env]} variant="h6">
            {process.env.VITE_APP_USING_RUNNER ? 'CI' : env}
          </Typography>
        </Box>
      ) : null}
      <Box ml={2}>
        <VersionRefresh />
      </Box>
      <ProfileControls />
    </>
  );
}

function Header(props: HeaderProps) {
  const {disableUrl, setIsSideMenuOpen} = props;
  const env = getEnvironment();
  return (
    <AppBar
      className="header"
      color="primary"
      sx={{backgroundColor: bgColors[env]}}
      position="fixed"
    >
      <Toolbar>
        <HeaderControls disableUrl={disableUrl} setIsSideMenuOpen={setIsSideMenuOpen} />
      </Toolbar>
    </AppBar>
  );
}

export const ManageProfileSideMenu: React.FC = () => {
  const handleSignOut = async () => {
    await signOut();

    const win: Window = window;
    win.location = '/';
  };

  return (
    <>
      <MenuItem
        label="Log out"
        href={null}
        onClick={() => handleSignOut()}
        icon={Logout}
        pathname=""
      />
    </>
  );
};

export default Header;
